import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './SelectPracticeConcept.css';

// ─── Sound Engine (Professional Mode) ─────────────────────────────────────────
const useProfessionalSoundEngine = () => {
  const ctx = useRef(null);

  const getCtx = () => {
    if (!ctx.current) ctx.current = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.current.state === 'suspended') ctx.current.resume();
    return ctx.current;
  };

  const playTone = useCallback((freq, type = 'sine', duration = 0.15, vol = 0.08, delay = 0) => {
    try {
      const ac = getCtx();
      const osc = ac.createOscillator();
      const gain = ac.createGain();
      osc.connect(gain); gain.connect(ac.destination);
      osc.type = type;
      osc.frequency.setValueAtTime(freq, ac.currentTime + delay);
      gain.gain.setValueAtTime(vol, ac.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + delay + duration);
      osc.start(ac.currentTime + delay);
      osc.stop(ac.currentTime + delay + duration);
    } catch (e) {}
  }, []);

  const sounds = {
    hover: () => playTone(660, 'sine', 0.08, 0.04),
    click: () => {
      playTone(523, 'sine', 0.1, 0.08);
      playTone(659, 'sine', 0.1, 0.06, 0.05);
    },
    expand: () => {
      playTone(392, 'sine', 0.12, 0.06);
      playTone(523, 'sine', 0.12, 0.06, 0.08);
    },
    collapse: () => {
      playTone(523, 'sine', 0.12, 0.06);
      playTone(392, 'sine', 0.12, 0.06, 0.08);
    },
    select: () => {
      playTone(587, 'sine', 0.15, 0.1);
      playTone(784, 'sine', 0.2, 0.08, 0.1);
    },
    launch: () => {
      [392, 523, 659, 784].forEach((f, i) => {
        playTone(f, 'sine', 0.1, 0.07, i * 0.08);
      });
    },
    urgent: () => {
      playTone(440, 'sine', 0.1, 0.12);
      playTone(440, 'sine', 0.1, 0.12, 0.15);
    },
    refresh: () => {
      playTone(880, 'sine', 0.06, 0.05);
      playTone(1046, 'sine', 0.08, 0.05, 0.08);
    },
    back: () => {
      playTone(523, 'sine', 0.08, 0.06);
      playTone(392, 'sine', 0.1, 0.06, 0.08);
    },
  };

  return sounds;
};

// ─── Gamified Sound Engine (K-5 Mode) ─────────────────────────────────────────
const useGamifiedSoundEngine = () => {
  const ctx = useRef(null);

  const getCtx = () => {
    if (!ctx.current) ctx.current = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.current.state === 'suspended') ctx.current.resume();
    return ctx.current;
  };

  const playTone = useCallback((freq, type = 'square', duration = 0.1, vol = 0.15, delay = 0) => {
    try {
      const ac = getCtx();
      const osc = ac.createOscillator();
      const gain = ac.createGain();
      osc.connect(gain); gain.connect(ac.destination);
      osc.type = type;
      osc.frequency.setValueAtTime(freq, ac.currentTime + delay);
      gain.gain.setValueAtTime(vol, ac.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + delay + duration);
      osc.start(ac.currentTime + delay);
      osc.stop(ac.currentTime + delay + duration);
    } catch (e) {}
  }, []);

  const sounds = {
    hover: () => playTone(880, 'sine', 0.06, 0.05),
    click: () => {
      playTone(440, 'square', 0.08, 0.12);
      playTone(660, 'square', 0.08, 0.10, 0.05);
    },
    expand: () => {
      playTone(330, 'sawtooth', 0.1, 0.1);
      playTone(440, 'sawtooth', 0.1, 0.1, 0.06);
      playTone(550, 'sawtooth', 0.1, 0.1, 0.12);
    },
    collapse: () => {
      playTone(550, 'sawtooth', 0.1, 0.1);
      playTone(440, 'sawtooth', 0.1, 0.1, 0.06);
      playTone(330, 'sawtooth', 0.1, 0.1, 0.12);
    },
    select: () => {
      playTone(523, 'square', 0.1, 0.15);
      playTone(659, 'square', 0.1, 0.15, 0.08);
      playTone(784, 'square', 0.15, 0.15, 0.16);
    },
    launch: () => {
      [261, 329, 392, 523, 659, 784, 1046].forEach((f, i) => {
        playTone(f, 'square', 0.12, 0.18, i * 0.055);
      });
    },
    urgent: () => {
      playTone(220, 'sawtooth', 0.08, 0.2);
      playTone(220, 'sawtooth', 0.08, 0.2, 0.15);
      playTone(293, 'sawtooth', 0.1, 0.2, 0.3);
    },
    refresh: () => {
      playTone(800, 'sine', 0.05, 0.1);
      playTone(1000, 'sine', 0.05, 0.1, 0.07);
      playTone(1200, 'sine', 0.1, 0.1, 0.14);
    },
    back: () => {
      playTone(600, 'sine', 0.05, 0.1);
      playTone(400, 'sine', 0.08, 0.1, 0.07);
    },
  };

  return sounds;
};

// ─── Particle System (Gamified Only) ─────────────────────────────────────────
const ParticleBurst = ({ x, y, onDone, isGamified }) => {
  useEffect(() => { const t = setTimeout(onDone, 800); return () => clearTimeout(t); }, [onDone]);
  if (!isGamified) return null;
  
  const particles = Array.from({ length: 12 }, (_, i) => ({
    id: i, angle: (i / 12) * 360,
    emoji: ['⚡', '✨', '🎮', '⭐', '💥', '🔥'][i % 6],
  }));
  return (
    <div className="particle-origin" style={{ left: x, top: y }}>
      {particles.map(p => (
        <div key={p.id} className="burst-particle" style={{ '--angle': `${p.angle}deg` }}>
          {p.emoji}
        </div>
      ))}
    </div>
  );
};

// ─── CRT Scanline Overlay (Gamified Only) ─────────────────────────────────────
const CRTOverlay = ({ isGamified }) => {
  if (!isGamified) return null;
  return <div className="crt-overlay" aria-hidden="true" />;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const SelectPracticeConcept = () => {
  const navigate = useNavigate();
  
  // Get user grade from localStorage or context (default to grade 3 for demo)
// Get user grade from API
const [userGrade, setUserGrade] = useState(null);
const [gradeLoading, setGradeLoading] = useState(true);
  
  const isGamified = userGrade !== null && userGrade <= 5;
  const sounds = isGamified ? useGamifiedSoundEngine() : useProfessionalSoundEngine();
  
  const [concepts, setConcepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedConcepts, setExpandedConcepts] = useState({});
  const [selectedSubconcept, setSelectedSubconcept] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [bursts, setBursts] = useState([]);
  const burstId = useRef(0);

  useEffect(() => { loadPracticeConcepts(); }, []);
  // Fetch user grade from profile API
useEffect(() => {
  const fetchUserGrade = async () => {
    try {
      const response = await fetch('/api/profile');
      const data = await response.json();
      if (data.success && data.user && data.user.grade) {
        setUserGrade(data.user.grade);
        // Also store in localStorage for persistence
        localStorage.setItem('user.grade', data.user.grade);
      } else {
        // Fallback to localStorage or default
        const saved = localStorage.getItem('user.grade');
        setUserGrade(saved ? parseInt(saved) : 5);
      }
    } catch (error) {
      console.error('Error fetching user grade:', error);
      // Fallback to localStorage or default
      const saved = localStorage.getItem('user.grade');
      setUserGrade(saved ? parseInt(saved) : 7);
    } finally {
      setGradeLoading(false);
    }
  };
  
  fetchUserGrade();
}, []);

  const addBurst = (x, y) => {
    if (!isGamified) return;
    const id = burstId.current++;
    setBursts(prev => [...prev, { id, x, y }]);
  };

  const removeBurst = (id) => setBursts(prev => prev.filter(b => b.id !== id));

  const loadPracticeConcepts = async () => {
    setLoading(true);
    sounds.refresh();
    try {
      const response = await fetch('/api/hierarchical-concept-progress');
      const data = await response.json();
      if (data.success && data.concepts?.length > 0) {
        const sorted = [...data.concepts].sort((a, b) => (a.overall_progress || 0) - (b.overall_progress || 0));
        setConcepts(sorted);
      } else {
        setConcepts([]);
      }
    } catch {
      setConcepts([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleSubconcepts = (index) => {
    const isExpanding = !expandedConcepts[index];
    isExpanding ? sounds.expand() : sounds.collapse();
    setExpandedConcepts(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const selectSubconcept = (name) => {
    sounds.select();
    setSelectedSubconcept(name);
  };

  const practiceSelectedSubconcept = (subconceptName, mainConcept, event) => {
    event?.stopPropagation();
    const target = subconceptName || selectedSubconcept;
    if (target) {
      sounds.launch();
      if (event) addBurst(event.clientX, event.clientY);
      setTimeout(() => {
        navigate(`/gamified-practice?concept=${encodeURIComponent(mainConcept)}&subconcept=${encodeURIComponent(target)}`);
      }, 600);
    } else {
      sounds.urgent();
      if (isGamified) {
        alert('⚡ Select a topic to start your quest! ⚡');
      } else {
        alert('Please select a topic to continue.');
      }
    }
  };

  // ── Helpers ──
  const getConceptIcon = (name) => {
    const map = { fractions: '⚡', algebra: '🔮', geometry: '📐', numbers: '🔢', measurement: '📏', data: '📊', probability: '🎲', calculus: '∫' };
    const lower = name.toLowerCase();
    return Object.entries(map).find(([k]) => lower.includes(k))?.[1] ?? (isGamified ? '🎯' : '📚');
  };

  const getProgressColor = (p) => {
    if (isGamified) {
      return p >= 80 ? '#00FF88' : p >= 60 ? '#FFD700' : p >= 40 ? '#FF6B35' : '#FF3366';
    }
    return p >= 80 ? '#2E7D32' : p >= 60 ? '#ED6C02' : p >= 40 ? '#F57C00' : '#D32F2F';
  };

  const getRarityData = (score) => {
    if (isGamified) {
      if (score >= 160) return { color: '#FFD700', badge: '👑 LEGENDARY', cls: 'legendary' };
      if (score >= 120) return { color: '#A855F7', badge: '✨ EPIC', cls: 'epic' };
      if (score >= 80)  return { color: '#3B82F6', badge: '⭐ RARE', cls: 'rare' };
      if (score >= 40)  return { color: '#10B981', badge: '🟢 UNCOMMON', cls: 'uncommon' };
      return { color: '#6B7280', badge: '⚪ COMMON', cls: 'common' };
    }
    // Professional mode - cleaner badges
    if (score >= 160) return { color: '#1B5E20', badge: 'MASTERED', cls: 'mastered' };
    if (score >= 120) return { color: '#2E7D32', badge: 'ADVANCED', cls: 'advanced' };
    if (score >= 80)  return { color: '#ED6C02', badge: 'PROFICIENT', cls: 'proficient' };
    if (score >= 40)  return { color: '#F57C00', badge: 'DEVELOPING', cls: 'developing' };
    return { color: '#757575', badge: 'BEGINNER', cls: 'beginner' };
  };

  const getSubconceptIcon = (name) => {
    if (isGamified) {
      const map = { addition: '➕', subtraction: '➖', multiplication: '✖️', division: '➗', fraction: '½', decimal: '🔟', percentage: '%', algebra: '𝑥', geometry: '📐', measurement: '📏', data: '📊', problem: '💡', word: '📝' };
      const lower = name.toLowerCase();
      return Object.entries(map).find(([k]) => lower.includes(k))?.[1] ?? '🎮';
    }
    // Professional mode - cleaner icons
    const map = { addition: '+', subtraction: '-', multiplication: '×', division: '÷', fraction: 'a/b', decimal: '0.5', percentage: '%', algebra: 'x', geometry: '∠', measurement: 'mm', data: '📈', problem: '?', word: 'abc' };
    const lower = name.toLowerCase();
    return Object.entries(map).find(([k]) => lower.includes(k))?.[1] ?? '📘';
  };

  const getMasteryEmoji = (m) => {
    if (isGamified) {
      return m >= 80 ? '🏆' : m >= 60 ? '🎖️' : m >= 30 ? '📈' : '🌱';
    }
    return '';
  };

  const formatLastPracticed = (lp) => {
    if (!lp) return 'Never';
    const diff = Math.floor((Date.now() - new Date(lp)) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return `${diff}d ago`;
    if (diff < 30) return `${Math.floor(diff / 7)}w ago`;
    return `${Math.floor(diff / 30)}m ago`;
  };

  // ── Render Sub-quests (Professional Mode) ──
  const renderSubconceptsProfessional = (subconcepts, mainConcept) => {
    if (!subconcepts?.length) {
      return (
        <div className="empty-state">
          <div className="empty-state-icon">📭</div>
          <p>No sub-topics available</p>
        </div>
      );
    }
    const sorted = [...subconcepts].sort((a, b) => {
      const sA = (a.accuracy || 0) + (a.mastery_level || 0);
      const sB = (b.accuracy || 0) + (b.mastery_level || 0);
      return sB - sA;
    });

    return (
      <div className="professional-grid">
        {sorted.map((sc, idx) => {
          const score = (sc.accuracy || 0) + (sc.mastery_level || 0);
          const isUrgent = score < 100;
          const rarity = getRarityData(score);
          const cardKey = `${mainConcept}-${sc.subconcept_name}`;
          const isSelected = selectedSubconcept === sc.subconcept_name;

          return (
            <div
              key={idx}
              className={`professional-card ${rarity.cls} ${isUrgent ? 'needs-attention' : ''} ${isSelected ? 'selected' : ''}`}
              onClick={() => { sounds.click(); selectSubconcept(sc.subconcept_name); }}
              onMouseEnter={() => { sounds.hover(); setHoveredCard(cardKey); }}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="card-header">
                <span className="card-icon">{getSubconceptIcon(sc.subconcept_name)}</span>
                <span className="card-badge" style={{ backgroundColor: rarity.color + '20', color: rarity.color }}>
                  {rarity.badge}
                </span>
              </div>
              
              <h3 className="card-title">{sc.subconcept_name}</h3>
              
              <div className="stats-row">
                <div className="stat">
                  <span className="stat-value">{sc.mastery_level || 0}%</span>
                  <span className="stat-label">Mastery</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{sc.accuracy || 0}%</span>
                  <span className="stat-label">Accuracy</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{sc.total_attempts || 0}</span>
                  <span className="stat-label">Attempts</span>
                </div>
              </div>
              
              <div className="progress-section">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${sc.mastery_level || 0}%`, backgroundColor: rarity.color }} />
                </div>
              </div>
              
              <div className="card-footer">
                <span className="last-practiced">Last: {formatLastPracticed(sc.last_practiced)}</span>
                <button 
                  className={`action-btn ${isUrgent ? 'warning' : 'primary'}`}
                  onClick={(e) => practiceSelectedSubconcept(sc.subconcept_name, mainConcept, e)}
                >
                  {isUrgent ? 'Review Now' : 'Start Practice'}
                </button>
              </div>
              
              {isSelected && <div className="selected-indicator">✓ Selected</div>}
            </div>
          );
        })}
      </div>
    );
  };

  // ── Render Sub-quests (Gamified Mode) ──
  const renderSubconceptsGamified = (subconcepts, mainConcept) => {
    if (!subconcepts?.length) {
      return (
        <div className="empty-dungeon">
          <div className="empty-dungeon-icon">🏚️</div>
          <p>No quests found in this dungeon!</p>
        </div>
      );
    }
    const sorted = [...subconcepts].sort((a, b) => {
      const sA = (a.accuracy || 0) + (a.mastery_level || 0);
      const sB = (b.accuracy || 0) + (b.mastery_level || 0);
      const aLow = sA < 100, bLow = sB < 100;
      if (aLow && !bLow) return -1;
      if (!aLow && bLow) return 1;
      return sA - sB;
    });

    return (
      <div className="quest-grid">
        {sorted.map((sc, idx) => {
          const score = (sc.accuracy || 0) + (sc.mastery_level || 0);
          const isUrgent = score < 100;
          const rarity = getRarityData(score);
          const cardKey = `${mainConcept}-${sc.subconcept_name}`;
          const isSelected = selectedSubconcept === sc.subconcept_name;
          const isHovered = hoveredCard === cardKey;

          return (
            <div
              key={idx}
              className={`quest-card rarity-${rarity.cls} ${isUrgent ? 'urgent' : ''} ${isSelected ? 'selected' : ''}`}
              style={{ '--rc': rarity.color }}
              onClick={() => { sounds.click(); selectSubconcept(sc.subconcept_name); }}
              onMouseEnter={() => { sounds.hover(); setHoveredCard(cardKey); }}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="card-accent" />
              <span className="corner tl" />
              <span className="corner tr" />
              <span className="corner bl" />
              <span className="corner br" />
              <div className="card-glow" />

              <div className="qcard-top">
                <div className="qcard-icon">{getSubconceptIcon(sc.subconcept_name)}</div>
                <div className="qcard-rarity" style={{ color: rarity.color, borderColor: rarity.color + '44' }}>
                  {rarity.badge}
                </div>
              </div>

              <div className="qcard-title">
                {isUrgent && <span className="flame-icon">🔥</span>}
                {sc.subconcept_name}
              </div>

              <div className="qcard-stats">
                <div className="qstat">
                  <div className="qstat-val">{sc.mastery_level || 0}%</div>
                  <div className="qstat-lbl">Mastery</div>
                  <div className="qstat-ico">{getMasteryEmoji(sc.mastery_level || 0)}</div>
                </div>
                <div className="qstat-sep" />
                <div className="qstat">
                  <div className="qstat-val">{sc.accuracy || 0}%</div>
                  <div className="qstat-lbl">Accuracy</div>
                  <div className="qstat-ico">🎯</div>
                </div>
                <div className="qstat-sep" />
                <div className="qstat">
                  <div className="qstat-val">{sc.total_attempts || 0}</div>
                  <div className="qstat-lbl">Battles</div>
                  <div className="qstat-ico">⚔️</div>
                </div>
              </div>

              <div className="qcard-prog">
                <div className="prog-labels">
                  <span>Mastery</span><span>{sc.mastery_level || 0}%</span>
                </div>
                <div className="prog-track">
                  <div className="prog-fill" style={{ width: `${sc.mastery_level || 0}%`, background: `linear-gradient(90deg, ${rarity.color}, ${rarity.color}BB)` }} />
                  <div className="prog-shimmer" style={{ width: `${sc.mastery_level || 0}%` }} />
                </div>
              </div>

              <div className="qcard-foot">
                <div className="qcard-time">🕒 {formatLastPracticed(sc.last_practiced)}</div>
                <button
                  className={`qcard-btn ${isUrgent ? 'btn-emergency' : ''}`}
                  onClick={(e) => practiceSelectedSubconcept(sc.subconcept_name, mainConcept, e)}
                >
                  {isHovered
                    ? '⚡ START QUEST ⚡'
                    : isUrgent
                    ? '🚨 TRAIN NOW'
                    : '▶ BEGIN'}
                </button>
              </div>

              {isSelected && <div className="selected-badge">✔ SELECTED</div>}
            </div>
          );
        })}
      </div>
    );
  };

  // ── Render Realms (Professional Mode) ──
  const renderRealmsProfessional = () => {
    if (!concepts?.length) {
      return (
        <div className="empty-state-full">
          <div className="empty-state-icon">📚</div>
          <h3>No Topics Available</h3>
          <p>Your learning materials will appear here.</p>
          <button className="btn-primary" onClick={() => { sounds.click(); navigate('/concept-selection'); }}>
            Browse Topics
          </button>
        </div>
      );
    }

    return (
      <div className="professional-topics">
        {concepts.map((concept, i) => {
          const progress = concept.overall_progress || 0;
          const isExpanded = expandedConcepts[i];

          return (
            <div key={i} className={`topic-section ${isExpanded ? 'expanded' : ''}`}>
              <div
                className="topic-header"
                onClick={() => toggleSubconcepts(i)}
              >
                <div className="topic-info">
                  <span className="topic-icon">{getConceptIcon(concept.concept_name)}</span>
                  <div className="topic-details">
                    <h2 className="topic-name">{concept.concept_name}</h2>
                    <div className="topic-stats">
                      <span>{concept.total_subconcepts || 0} subtopics</span>
                      <span>{concept.mastered_subconcepts || 0} mastered</span>
                    </div>
                  </div>
                </div>
                <div className="topic-progress">
                  <div className="progress-circle">
                    <svg viewBox="0 0 36 36">
                      <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#E0E0E0" strokeWidth="3"/>
                      <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke={getProgressColor(progress)} strokeWidth="3" strokeDasharray={`${progress}, 100`}/>
                    </svg>
                    <span className="progress-percent">{Math.round(progress)}%</span>
                  </div>
                  <span className={`expand-icon ${isExpanded ? 'rotated' : ''}`}>▼</span>
                </div>
              </div>

              <div className={`topic-content ${isExpanded ? 'open' : ''}`}>
                {renderSubconceptsProfessional(concept.subconcepts, concept.concept_name)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Render Realms (Gamified Mode) ──
  const renderRealmsGamified = () => {
    if (!concepts?.length) {
      return (
        <div className="empty-realm">
          <div className="empty-realm-skull">🏰</div>
          <h3 className="empty-realm-title">NO REALMS UNLOCKED</h3>
          <p>Your quest board is empty, hero. Choose your battles!</p>
          <button className="pixel-btn gold" onClick={() => { sounds.click(); navigate('/concept-selection'); }}>
            ⚔️ EXPLORE REALMS
          </button>
        </div>
      );
    }

    return (
      <div className="realms-list">
        {concepts.map((concept, i) => {
          const progress = concept.overall_progress || 0;
          const isExpanded = expandedConcepts[i];
          const level = progress < 30 ? { label: 'NOVICE', color: '#FFD700' }
            : progress < 60 ? { label: 'APPRENTICE', color: '#10B981' }
            : progress < 80 ? { label: 'MASTER', color: '#3B82F6' }
            : { label: 'GRANDMASTER', color: '#A855F7' };

          return (
            <div key={i} className={`realm-block ${isExpanded ? 'realm-open' : ''}`}>
              <div
                className="realm-header"
                onClick={() => toggleSubconcepts(i)}
                role="button"
                tabIndex={0}
              >
                <div className="realm-left">
                  <div className="realm-icon-box">
                    {getConceptIcon(concept.concept_name)}
                  </div>
                  <div className="realm-meta">
                    <div className="realm-name-row">
                      <span className="realm-name">{concept.concept_name}</span>
                      <span className="realm-level-badge" style={{ color: level.color, borderColor: level.color + '44' }}>
                        {level.label}
                      </span>
                    </div>
                    <div className="realm-sub-row">
                      <span>📚 {concept.total_subconcepts || 0} Quests</span>
                      <span>✅ {concept.selected_subconcepts || 0} Active</span>
                      <span>🏆 {concept.mastered_subconcepts || 0} Mastered</span>
                    </div>
                  </div>
                </div>

                <div className="realm-xp">
                  <div className="realm-xp-track">
                    <div className="realm-xp-fill" style={{ width: `${progress}%`, background: getProgressColor(progress) }} />
                  </div>
                  <div className="realm-xp-label" style={{ color: getProgressColor(progress) }}>
                    {Math.round(progress)}% XP
                  </div>
                </div>

                <div className={`realm-arrow ${isExpanded ? 'open' : ''}`}>▼</div>
              </div>

              <div className={`realm-quests-wrap ${isExpanded ? 'open' : ''}`}>
                <div className="realm-quests-inner">
                  {renderSubconceptsGamified(concept.subconcepts, concept.concept_name)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Loading Screen ──
 // ── Loading Screen ──
if (loading || gradeLoading) {
  return (
    <div className={`loading-screen ${(isGamified && userGrade !== null) ? 'gamified' : 'professional'}`}>
      <CRTOverlay isGamified={isGamified && userGrade !== null} />
      <div className="loading-inner">
        <div className="loading-logo">{(!gradeLoading && isGamified) ? '🎮' : '📚'}</div>
        <div className="loading-title">{(!gradeLoading && isGamified) ? 'LOADING QUESTS' : 'Loading Content'}</div>
        <div className="loading-bar-wrap">
          <div className="loading-bar-fill" />
        </div>
        <div className="loading-dots">
          <span /><span /><span />
        </div>
      </div>
    </div>
  );
}

  // ── Main Render ──
  return (
    <div className={`${isGamified ? 'gp-root' : 'professional-root'}`}>
      <CRTOverlay isGamified={isGamified} />

      {isGamified && bursts.map(b => (
        <ParticleBurst key={b.id} x={b.x} y={b.y} onDone={() => removeBurst(b.id)} isGamified={isGamified} />
      ))}

      {isGamified && <div className="bg-grid" />}
      {isGamified && <div className="bg-vignette" />}

      <div className={isGamified ? 'gp-shell' : 'professional-shell'}>

        {/* ── Top Header ── */}
        <header className={isGamified ? 'hud-bar' : 'professional-header'}>
          <div className={isGamified ? 'hud-left' : 'header-left'}>
            <div className={isGamified ? 'hud-logo' : 'header-logo'}>
              {isGamified ? '🎮' : '📘'}
            </div>
            <div className={isGamified ? 'hud-titles' : 'header-titles'}>
              <div className={isGamified ? 'hud-main-title' : 'main-title'}>
                Practice{isGamified ? <span className="title-accent">BOARD</span> : ' Center'}
              </div>
              <div className={isGamified ? 'hud-sub' : 'sub-title'}>
                {isGamified ? 'Choose your learning adventure' : 'Select a topic to begin'}
              </div>
            </div>
          </div>
          <div className={isGamified ? 'hud-right' : 'header-right'}>
            <div className={isGamified ? 'hud-stat' : 'stat-card'}>
              <span className="stat-ico">{isGamified ? '🏆' : '📚'}</span>
              <span className="stat-val">{concepts.reduce((a, c) => a + (c.total_subconcepts || 0), 0)}</span>
              <span className="stat-lbl">Topics</span>
            </div>
            <div className={isGamified ? 'hud-stat' : 'stat-card'}>
              <span className="stat-ico">{isGamified ? '⚡' : '📖'}</span>
              <span className="stat-val">{concepts.length}</span>
              <span className="stat-lbl">Categories</span>
            </div>
          </div>
        </header>

        {/* ── Rarity/Progress Bar (Gamified only) ── */}
        {isGamified && (
          <div className="rarity-bar">
            {[
              { color: '#FFD700', label: '👑 LEGENDARY 160+' },
              { color: '#A855F7', label: '✨ EPIC 120+' },
              { color: '#3B82F6', label: '⭐ RARE 80+' },
              { color: '#10B981', label: '🟢 UNCOMMON 40+' },
              { color: '#6B7280', label: '⚪ COMMON 0+' },
              { color: '#FF3366', label: '🔥 EMERGENCY <100' },
            ].map((r, i) => (
              <div key={i} className="rarity-pill" style={{ '--rc': r.color }}>
                {r.label}
              </div>
            ))}
          </div>
        )}

        {/* ── Professional Mode Filter Bar ── */}
        {!isGamified && (
          <div className="professional-filter-bar">
            <button className="filter-btn active">All Topics</button>
            <button className="filter-btn">Mastered</button>
            <button className="filter-btn">In Progress</button>
            <button className="filter-btn">Needs Review</button>
          </div>
        )}

        {/* ── Main Content ── */}
        <main className={isGamified ? 'gp-content' : 'professional-content'}>
          {isGamified ? renderRealmsGamified() : renderRealmsProfessional()}
        </main>

        {/* ── Bottom HUD ── */}
        <footer className={isGamified ? 'bottom-hud' : 'professional-footer'}>
          <button
            className={isGamified ? 'pixel-btn silver' : 'secondary-btn'}
            onClick={() => { sounds.back(); navigate('/dashboard'); }}
          >
            {isGamified ? '◄ BACK TO HUB' : '← Back to Dashboard'}
          </button>
          <button
            className={isGamified ? 'pixel-btn gold' : 'primary-btn'}
            onClick={() => { sounds.refresh(); loadPracticeConcepts(); }}
          >
            {isGamified ? '↺ REFRESH QUESTS' : '↻ Refresh'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default SelectPracticeConcept;